import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { profileClient } from "@/shared/api/clients";
import { POSITIONS } from "../../shared/constants/userConstants";
import { OfficerProfile } from "../../shared/interfaces/OfficerProfile";
import { ProfileResponse } from "../../shared/interfaces/ProfileResponse";
import {
  ProfileMetadataValues,
  profileMetadataSchema,
} from "../../shared/utils/userValidation";

interface ProfileMetadataFormProps {
  officer: OfficerProfile;
  onSaved: (updated: OfficerProfile) => void;
}

const officerValues = (officer: OfficerProfile): ProfileMetadataValues => ({
  badgeNumber: officer.badgeNumber ?? "",
  position: officer.rank ?? "",
  department: officer.department ?? "",
});

const ProfileMetadataForm: React.FC<ProfileMetadataFormProps> = ({
  officer,
  onSaved,
}) => {
  // Synchronous in-flight guard: rapid clicks can re-enter the submit
  // handler before the disabled state has actually re-rendered.
  const submittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ProfileMetadataValues>({
    resolver: zodResolver(profileMetadataSchema),
    mode: "onBlur",
    defaultValues: officerValues(officer),
  });

  useEffect(() => {
    reset(officerValues(officer));
    setError(null);
    setSuccess(null);
  }, [officer, reset]);

  // zodResolver hands the parsed (trimmed) values to the submit handler.
  const handleSave = handleSubmit(async (values) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setError(null);
    setSuccess(null);
    try {
      const response = await profileClient.put<ProfileResponse>("/v1/profile", {
        email: officer.email,
        firstName: officer.firstName,
        lastName: officer.lastName,
        divisionName: values.department,
        jobTitle: values.position,
        badgeNumber: values.badgeNumber,
      });
      const { user, memberships } = response.data;
      const updated: OfficerProfile = {
        ...officer,
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        department: user.metadata.department,
        rank: user.metadata.position,
        badgeNumber: user.metadata.badge_number,
        stationName: memberships[0]?.organizationName ?? officer.stationName,
        status: memberships[0]?.status ?? officer.status,
      };
      onSaved(updated);
      setSuccess("Profile details updated.");
    } catch (e) {
      const message = isAxiosError(e) ? e.response?.data?.error : null;
      setError(message || "Failed to update profile. Please try again.");
    } finally {
      submittingRef.current = false;
    }
  });

  const handleReset = () => {
    reset(officerValues(officer));
    setError(null);
    setSuccess(null);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 3 }} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          Department Details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Update your badge number, job title, and department.
        </Typography>
      </Box>

      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Badge Number"
              required
              fullWidth
              disabled={isSubmitting}
              error={!!errors.badgeNumber}
              helperText={errors.badgeNumber?.message ?? "1-10 digits"}
              inputProps={{ inputMode: "numeric", maxLength: 10 }}
              {...register("badgeNumber")}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="position"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  required
                  disabled={isSubmitting}
                  error={!!errors.position}
                >
                  <InputLabel id="profile-rank-label">Job Title</InputLabel>
                  <Select labelId="profile-rank-label" label="Job Title" {...field}>
                    {POSITIONS.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.position?.message ?? ""}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Department"
              required
              fullWidth
              disabled={isSubmitting}
              error={!!errors.department}
              helperText={errors.department?.message ?? ""}
              {...register("department")}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            onClick={handleReset}
            disabled={isSubmitting || !isDirty}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Reset
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSubmitting || !isDirty}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default ProfileMetadataForm;
